import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Lightbulb, Users, Target, Heart, Shield, ArrowRight } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Lightbulb className="h-12 w-12" />,
      title: "Innovation",
      description: "Creating smart solutions for real-world challenges through cutting-edge technology and creative thinking."
    },
    {
      icon: <Shield className="h-12 w-12" />,
      title: "Integrity",
      description: "Building trust through honesty, transparency, and ethical business practices in everything we do."
    },
    {
      icon: <Users className="h-12 w-12" />,
      title: "Collaboration",
      description: "Working together with clients, partners, and communities for meaningful progress and mutual success."
    },
    {
      icon: <Target className="h-12 w-12" />,
      title: "Impact",
      description: "Turning innovative ideas into practical solutions that truly make a difference in people's lives."
    },
    {
      icon: <Heart className="h-12 w-12" />,
      title: "Compassion",
      description: "Giving back to communities and people in need through our humanitarian initiatives and social responsibility."
    }
  ];

  const leaders = [
    {
      id: 'keyvin-runtuwarouw',
      name: 'Keyvin Runtuwarouw',
      title: 'Chief Executive Officer',
      image: '/assets/Keyvin%20Runtuwarouw%204x3.jpg',
      shortBio: 'Visionary leader driving innovation and growth at Vincotek with extensive experience in technology and business strategy.'
    },
    {
      id: 'Rey-Runtuwarouw',
      name: 'Rey Runtuwarouw',
      title: 'Chief Corporate Officer',
      image: '/assets/Margareta%20Tanuwijaya%204x3.jpg',
      shortBio: 'Strategic executive focused on corporate development, partnerships, and organizational excellence.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-blue-50/30 to-yellow-50/20 py-20 lg:py-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <h1 className="text-4xl md:text-5xl font-bold text-vincotek-dark leading-tight">
                Building the{' '}
                <span className="bg-gradient-to-r from-vincotek-yellow to-vincotek-blue bg-clip-text text-transparent">
                  Future
                </span>{' '}
                of Technology
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Vincotek is a forward-thinking technology startup based in Australia, 
                dedicated to building accessible digital solutions that empower people and simplify work.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/about/values">
                  <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full transition-all duration-300 hover:shadow-lg group">
                    Our Values
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/about/leadership">
                  <Button variant="outline" className="border-vincotek-yellow text-vincotek-dark hover:bg-vincotek-yellow px-8 py-3 rounded-full transition-all duration-300">
                    Meet Our Team
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="/assets/building-the-future.jpg" 
                alt="Building The Future" 
                className="w-full h-96 object-cover rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-6 shadow-xl border border-gray-100">
                <div className="text-vincotek-dark text-lg font-bold">2025</div>
                <div className="text-gray-600 text-sm">Founded with Purpose</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-6">
                Who We Are
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed">
                Vincotek is a new technology company focused on building accessible digital solutions 
                that make a meaningful difference in how people live and work. We believe technology 
                should empower everyone, not just a select few.
              </p>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-vincotek-dark">Our Story</h3>
                <p className="text-gray-600 leading-relaxed">
                  Founded in Australia, Vincotek emerged from a simple belief: technology should serve 
                  humanity, not the other way around. Our team brings together diverse expertise in 
                  software development, business strategy, and social impact to create solutions that 
                  truly matter.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We're not just building software – we're crafting experiences that simplify complexity, 
                  bridge gaps, and unlock potential for individuals and businesses alike.
                </p>
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1577962917302-cd874c4e31d2" 
                  alt="Professional business meeting" 
                  className="w-full h-80 object-cover rounded-xl shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-8">
              Our Mission
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-12">
              Vincotek's purpose is clear: empowering people and simplifying work through technology. 
              We develop digital solutions that are not only innovative but also intuitive, accessible, 
              and designed with real human needs at the center.
            </p>
            
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-100">
              <blockquote className="text-2xl font-medium text-vincotek-dark italic mb-4">
                "Technology should amplify human potential, not complicate it."
              </blockquote>
              <p className="text-gray-600">
                This philosophy drives every decision we make, from the products we build to the 
                partnerships we form and the communities we serve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Humanitarian Purpose Section */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Our Humanitarian Purpose
              </h2>
              <div className="w-24 h-1 bg-gradient-to-r from-vincotek-yellow to-vincotek-blue mx-auto mb-8"></div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <div className="text-6xl font-bold text-vincotek-yellow mb-4">2.5%</div>
                <p className="text-xl text-gray-300 leading-relaxed">
                  Vincotek dedicates 2.5% of revenue from investments, product sales, and GoFella 
                  subscriptions to helping disadvantaged communities in Australia and other countries.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Through our humanitarian initiatives, we donate food, clothing, and essential goods 
                  to those who need them most. We believe that success should be shared, and that 
                  businesses have a responsibility to give back to the communities that support them.
                </p>
              </div>
              <div className="relative">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                  <h3 className="text-2xl font-semibold text-vincotek-yellow mb-6">Our Impact Areas</h3>
                  <ul className="space-y-4 text-gray-300">
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mr-3"></div>
                      Food security for disadvantaged families
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mr-3"></div>
                      Essential clothing and supplies
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mr-3"></div>
                      Community support programs
                    </li>
                    <li className="flex items-center">
                      <div className="w-2 h-2 bg-vincotek-yellow rounded-full mr-3"></div>
                      International humanitarian aid
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Preview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-4">
              Our Core Values
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide our decisions, shape our culture, and define our approach to business and life.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {values.slice(0, 3).map((value, index) => (
              <Card key={index} className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border-0 bg-gradient-to-br from-white to-gray-50/50">
                <CardContent className="p-8 text-center">
                  <div className="text-vincotek-blue mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-vincotek-dark mb-4">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center">
            <Link to="/about/values">
              <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                Explore All Our Values
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Leadership Preview */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-vincotek-dark mb-4">
              Our Leadership
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the visionary leaders driving Vincotek's mission and shaping the future of technology.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {leaders.map((leader) => (
              <Link key={leader.id} to={`/about/leadership/${leader.id}`}>
                <Card className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-2 cursor-pointer bg-white border-0">
                  <CardContent className="p-8 text-center">
                    <div className="w-32 h-32 rounded-full mx-auto mb-6 overflow-hidden group-hover:scale-105 transition-transform duration-300">
                      <img 
                        src={leader.image} 
                        alt={leader.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h3 className="text-xl font-semibold text-vincotek-dark mb-2">
                      {leader.name}
                    </h3>
                    <p className="text-vincotek-blue font-medium mb-4">
                      {leader.title}
                    </p>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {leader.shortBio}
                    </p>
                    <div className="mt-6">
                      <Button variant="outline" className="border-vincotek-blue text-vincotek-blue hover:bg-vincotek-blue hover:text-white rounded-full transition-all duration-300 group-hover:shadow-lg">
                        View Profile
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/about/leadership">
              <Button className="bg-vincotek-blue hover:bg-blue-600 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                Meet Our Full Team
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-vincotek-dark text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Join Our Mission
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Whether you're looking for innovative solutions, partnership opportunities, or want to be part of our team, 
            we'd love to connect with you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-vincotek-yellow hover:bg-yellow-400 text-vincotek-dark px-8 py-3 rounded-full font-semibold transition-all duration-300 hover:shadow-lg">
                Get in Touch
              </Button>
            </Link>
            <Link to="/careers">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-vincotek-dark px-8 py-3 rounded-full transition-all duration-300">
                Explore Careers
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;